import React, { useState, useEffect, useRef } from 'react';
import { HtmlEntityService } from '../../lib/services/htmlEntityService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType, HtmlEntityMode } from '../../lib/types';
import './HtmlEntityEncoder.css';

export const HtmlEntityEncoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [entityMode, setEntityMode] = useState<HtmlEntityMode>(HtmlEntityMode.Html4);
  const [includeExtended, setIncludeExtended] = useState(false);
  const [preserveFormatting, setPreserveFormatting] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleConvert = async () => {
    if (!input.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = `${mode}-${entityMode}-${includeExtended}-${preserveFormatting}-${input.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      const config = HtmlEntityService.createConfiguration(
        input,
        mode === 'encode' ? HtmlEntityMode.ENCODE : HtmlEntityMode.DECODE,
        {
          encodeAllChars: includeExtended,
          preserveLineBreaks: preserveFormatting
        }
      );

      let result;
      if (mode === 'encode') {
        result = await HtmlEntityService.encode(config);
      } else {
        result = await HtmlEntityService.decode(config);
      }

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        setOutput(result.outputData as string);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.HTML_ENTITY);
        setError(errorMessage);
        setOutput('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleConvert();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setError(null);
      setIsLoading(false);
    }
  }, [input, mode, entityMode, includeExtended, preserveFormatting]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClass = () => {
    const baseClass = 'copy-button';
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} success`;
      case 'error':
        return `${baseClass} error`;
      default:
        return baseClass;
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError(null);
    setCopyFeedback('idle');
  };

  const insertSampleText = (type: string) => {
    const samples = {
      basic: 'Hello <world> & "friends"!',
      html: '<div class="container">\n  <h1>Welcome to my site</h1>\n  <p>Visit <a href="https://example.com">Example.com</a></p>\n</div>',
      symbols: "© ™ ® ℠ € £ ¥ § ¶ † … – — \"\" '' •",
      math: '∑ ∏ ∫ ∆ ∇ ∂ √ ∞ ≠ ≤ ≥ ± × ÷ ∈ ∉ ∪ ∩ ⊂ ⊃ ⊆ ⊇',
      greek: 'α β γ δ ε ζ η θ ι κ λ μ ν ξ ο π ρ σ τ υ φ χ ψ ω Α Β Γ Δ Ε Ζ Η Θ Ι Κ Λ Μ Ν Ξ Ο Π Ρ Σ Τ Υ Φ Χ Ψ Ω'
    };

    setInput(samples[type as keyof typeof samples] || '');
  };

  return (
    <div className="html-entity-encoder">
      <h1>HTML Entity Encoder/Decoder</h1>
      <p>Encode HTML special characters to entities for safe display or decode entities back to original characters</p>

      <div className="encoder-container">
        <div className="form-group">
          <label htmlFor="mode">Operation Mode</label>
          <select
            id="mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            data-testid="mode-select"
          >
            <option value="encode">Encode Characters to Entities</option>
            <option value="decode">Decode Entities to Characters</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="entity-mode">Entity Standard</label>
          <select
            id="entity-mode"
            value={entityMode}
            onChange={(e) => setEntityMode(e.target.value as HtmlEntityMode)}
            data-testid="entity-mode-select"
          >
            <option value={HtmlEntityMode.Html4}>HTML 4 (Standard)</option>
            <option value={HtmlEntityMode.Html5}>HTML 5 (Extended)</option>
            <option value={HtmlEntityMode.Xml}>XML (Strict)</option>
            <option value={HtmlEntityMode.Numeric}>Numeric Only (&#nnn;)</option>
          </select>
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={includeExtended}
              onChange={(e) => setIncludeExtended(e.target.checked)}
              data-testid="extended-checkbox"
            />
            Include Extended Entities (mathematical symbols, Greek letters, etc.)
          </label>
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={preserveFormatting}
              onChange={(e) => setPreserveFormatting(e.target.checked)}
              data-testid="formatting-checkbox"
            />
            Preserve HTML Formatting (don't encode tags like &lt;b&gt;, &lt;i&gt;, etc.)
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="input-text">
            {mode === 'encode' ? 'Text to Encode' : 'Entities to Decode'}
          </label>
          <textarea
            id="input-text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              mode === 'encode'
                ? 'Enter text with HTML special characters to encode...'
                : 'Paste HTML entities to decode...'
            }
            className="input-textarea"
            rows={8}
            data-testid="input-text"
          />
        </div>

        <div className="sample-section">
          <label>Quick Samples:</label>
          <div className="sample-buttons">
            <button
              onClick={() => insertSampleText('basic')}
              className="sample-button"
              data-testid="sample-basic"
            >
              Basic HTML
            </button>
            <button
              onClick={() => insertSampleText('html')}
              className="sample-button"
              data-testid="sample-html"
            >
              HTML Document
            </button>
            <button
              onClick={() => insertSampleText('symbols')}
              className="sample-button"
              data-testid="sample-symbols"
            >
              Symbols
            </button>
            <button
              onClick={() => insertSampleText('math')}
              className="sample-button"
              data-testid="sample-math"
            >
              Mathematical
            </button>
            <button
              onClick={() => insertSampleText('greek')}
              className="sample-button"
              data-testid="sample-greek"
            >
              Greek Letters
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message" data-testid="error-message">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="loading-indicator" data-testid="loading-indicator">
            Processing HTML entities...
          </div>
        )}

        <div className="form-group">
          <label htmlFor="output-text">
            {mode === 'encode' ? 'Encoded Entities' : 'Decoded Text'}
          </label>
          <textarea
            id="output-text"
            value={output}
            readOnly
            placeholder="Result will appear here..."
            className="output-textarea"
            rows={8}
            data-testid="output-text"
          />
        </div>

        <div className="button-group">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClass()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>

          <button
            onClick={clearAll}
            className="clear-button"
            data-testid="clear-button"
          >
            Clear All
          </button>
        </div>

        <div className="reference-section">
          <h3>HTML Entity Reference</h3>

          <div className="reference-tabs">
            <div className="tab-group">
              <h4>Common Characters</h4>
              <table className="entity-table">
                <thead>
                  <tr>
                    <th>Character</th>
                    <th>Entity</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>&lt;</td>
                    <td>&amp;lt;</td>
                    <td>Less than</td>
                  </tr>
                  <tr>
                    <td>&gt;</td>
                    <td>&amp;gt;</td>
                    <td>Greater than</td>
                  </tr>
                  <tr>
                    <td>&amp;</td>
                    <td>&amp;amp;</td>
                    <td>Ampersand</td>
                  </tr>
                  <tr>
                    <td>"</td>
                    <td>&amp;quot;</td>
                    <td>Quotation mark</td>
                  </tr>
                  <tr>
                    <td>'</td>
                    <td>&amp;apos;</td>
                    <td>Apostrophe</td>
                  </tr>
                  <tr>
                    <td>&nbsp;</td>
                    <td>&amp;nbsp;</td>
                    <td>Non-breaking space</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="tab-group">
              <h4>Symbols & Punctuation</h4>
              <table className="entity-table">
                <thead>
                  <tr>
                    <th>Character</th>
                    <th>Entity</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>©</td>
                    <td>&amp;copy;</td>
                    <td>Copyright</td>
                  </tr>
                  <tr>
                    <td>®</td>
                    <td>&amp;reg;</td>
                    <td>Registered trademark</td>
                  </tr>
                  <tr>
                    <td>™</td>
                    <td>&amp;trade;</td>
                    <td>Trademark</td>
                  </tr>
                  <tr>
                    <td>€</td>
                    <td>&amp;euro;</td>
                    <td>Euro</td>
                  </tr>
                  <tr>
                    <td>£</td>
                    <td>&amp;pound;</td>
                    <td>Pound</td>
                  </tr>
                  <tr>
                    <td>¥</td>
                    <td>&amp;yen;</td>
                    <td>Yen</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="tab-group">
              <h4>Mathematical (Extended)</h4>
              <table className="entity-table">
                <thead>
                  <tr>
                    <th>Character</th>
                    <th>Entity</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>±</td>
                    <td>&amp;plusmn;</td>
                    <td>Plus-minus</td>
                  </tr>
                  <tr>
                    <td>×</td>
                    <td>&amp;times;</td>
                    <td>Multiplication</td>
                  </tr>
                  <tr>
                    <td>÷</td>
                    <td>&amp;divide;</td>
                    <td>Division</td>
                  </tr>
                  <tr>
                    <td>≤</td>
                    <td>&amp;le;</td>
                    <td>Less than or equal</td>
                  </tr>
                  <tr>
                    <td>≥</td>
                    <td>&amp;ge;</td>
                    <td>Greater than or equal</td>
                  </tr>
                  <tr>
                    <td>≠</td>
                    <td>&amp;ne;</td>
                    <td>Not equal</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="usage-tips">
            <h4>📝 Usage Tips</h4>
            <ul>
              <li><strong>HTML 4:</strong> Most common entities, widely supported</li>
              <li><strong>HTML 5:</strong> Additional entities for modern content</li>
              <li><strong>XML:</strong> Strict XML compliance, fewer entities</li>
              <li><strong>Numeric:</strong> Always uses numeric entities (&amp;#123;)</li>
              <li><strong>Preserve Formatting:</strong> Keeps HTML tags intact for rich text</li>
              <li><strong>Extended Entities:</strong> Includes mathematical, Greek, and special symbols</li>
            </ul>
          </div>
        </div>
      </div>

      {/* No-JS Fallback */}
      <noscript>
        <div className="fallback-form">
          <form method="POST" action="/api/html-entity" aria-label="HTML Entity Fallback Form">
            <div className="form-group">
              <label htmlFor="fallback-mode">Mode:</label>
              <select name="mode" id="fallback-mode" aria-label="Select encoding mode">
                <option value="encode">Encode</option>
                <option value="decode">Decode</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="fallback-input">Input:</label>
              <textarea name="input" id="fallback-input" rows={8} required aria-label="Input text" />
            </div>
            <div className="form-group">
              <label htmlFor="fallback-extended">Include Extended:</label>
              <input type="checkbox" name="extended" id="fallback-extended" aria-label="Include extended entities" />
            </div>
            <button type="submit" aria-label="Process HTML entities">Process</button>
          </form>
        </div>
      </noscript>
    </div>
  );
};